import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { createElement, useEffect, useState } from 'react';
import { E as EGDSButtonSize } from '../button/index-chunk.esm.js';
import { E as EGDSPrimaryButton } from '../primary-button/index-chunk.esm.js';
import { c as classNames, l as liveAnnounce, d as EGDSContextProvider, L as Level } from '../theme-provider/index-chunk.esm.js';
import { E as EGDSTertiaryButton } from '../tertiary-button/index-chunk.esm.js';
import { a as EGDSIcon, E as EGDSIconSize } from '../icon/index-chunk.esm.js';
import { i as EGDSLayoutFlex, j as EGDSLayoutFlexItem } from '../layout-flex-item/index-chunk.esm.js';
import { b as EGDSSpacing } from '../spacing-hr/index-chunk.esm.js';
import { b as EGDSPagingPrevButton, a as EGDSPagingNextButton } from '../paging-prev-button/index-chunk.esm.js';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';
import { e as EGDSFullscreenDialog, E as EGDSDialogAnimationType } from '../fullscreen-dialog/index-chunk.esm.js';
import { b as EGDSTextInput, a as EGDSFauxInput } from '../swapper-input/index-chunk.esm.js';
import { E as EGDSMenu, b as EGDSMenuTrigger, c as EGDSMenuContainer, P as PositionType } from '../menu-icon-trigger/index-chunk.esm.js';
import styles from '../styles/uitk-egds-date-picker.css';
import { E as EGDSFloatingFullWidthButton } from '../floating-full-width-button/index-chunk.esm.js';
import { E as EGDSScrim, a as EGDSScrimType } from '../scrim/index-chunk.esm.js';
import { b as EGDSToolbar, e as EGDSToolbarButton } from '../toolbar-button/index-chunk.esm.js';
import { i as isElementVisible } from '../is-element-visible/index-chunk.esm.js';

/* eslint-disable no-param-reassign */
const DAYS_IN_MS = 24 * 60 * 60 * 1000;
class DatePickerModel {
  isSelectingStart = false;
  isSelectingEnd = false;
  start;
  end;
  lastValidEndDate;
  singleDateSelect = false;
  holidays;
  holidaysName;
  holidaysConfigured;
  dayHolidays;
  isNotFirstTimeSameLength;
  constructor(endDate, singleDateSelect) {
    this.lastValidEndDate = endDate;
    this.singleDateSelect = singleDateSelect;
    this.dayHolidays = {};
    this.isNotFirstTimeSameLength = false;
  }
  get numberOfNights() {
    if (this.start && this.end) {
      return Math.round(Math.abs((this.end.getTime() - this.start.getTime()) / DAYS_IN_MS));
    }
    return 0;
  }
  // Helper method to valid if a given date is between start and end
  isDateSelected(date) {
    if (!this.start || !this.end) {
      return false;
    }
    this.setHolidaysSelection();
    return date.getTime() >= this.start.getTime() && date.getTime() <= this.end.getTime();
  }
  /*
   * Use this method to set dates for the model.
   * Setting start and end directly will result in bad logic
   * Notes:
   *  You may be tempted to simplify the logic below. Don't. This is easier to reason. Thanks :)
   */
  dateSelected(date) {
    // Nothing is selected
    if (!this.start && !this.end) {
      if (this.isSelectingStart) {
        this.start = date;
        this.setCurrentTrigger('end');
      } else if (this.isSelectingEnd) {
        this.end = date;
        this.setCurrentTrigger('start');
      } else {
        this.resetAndSetStart(date);
      }
    } else if (this.start && this.end) {
      // Both dates are selected
      if (this.isSelectingStart) {
        // Keep the same length of stay when reselecting start date
        this.selectStartWithSameLengthOfStay(date);
      } else if (this.isSelectingEnd) {
        this.selectEndWithAvailableStart(date);
      } else {
        this.resetAndSetStart(date);
      }
    } else if (this.start && !this.end) {
      // Only Start date is selected
      if (this.isSelectingStart) {
        this.start = date;
        this.setCurrentTrigger('end');
      } else if (this.isSelectingEnd) {
        this.selectEndWithAvailableStart(date);
      } else {
        this.resetAndSetStart(date);
      }
    } else {
      // Only End date is selected
      // eslint-disable-next-line no-lonely-if
      if (this.isSelectingStart) {
        this.selectStartWithAvailableEnd(date);
      } else if (this.isSelectingEnd) {
        this.end = date;
        this.setCurrentTrigger('start');
      } else {
        this.resetAndSetStart(date);
      }
    }
    this.setHolidaysSelection();
  }
  /*
   * Use this method to set single date for the model.
   */
  singleDateSelected(date) {
    this.start = date;
    this.end = undefined;
    this.setCurrentTrigger('start');
    this.setHolidaysSelection();
  }
  // Use this method to switch current trigger (TopBar)
  setCurrentTrigger(trigger) {
    if (trigger === 'start') {
      this.isSelectingStart = true;
      this.isSelectingEnd = false;
    } else if (trigger === 'end') {
      this.isSelectingStart = false;
      this.isSelectingEnd = true;
    } else {
      this.isSelectingStart = false;
      this.isSelectingEnd = false;
    }
  }
  // Private helper functions for dateSelected(date)
  selectEndWithAvailableStart(date) {
    if (this.start && (date.getTime() < this.start.getTime() || date.getTime() === this.start.getTime() && !this.singleDateSelect)) {
      this.resetAndSetStart(date);
    } else {
      this.end = date;
      this.setCurrentTrigger('start');
    }
  }
  selectStartWithAvailableEnd(date) {
    if (this.end && date.getTime() >= this.end.getTime()) {
      this.resetAndSetStart(date);
    } else {
      this.start = date;
      this.setCurrentTrigger('end');
    }
  }
  selectStartWithSameLengthOfStay(date) {
    const sameLengthOfStayEndDate = new Date(date.getTime() + this.numberOfNights * DAYS_IN_MS); // End must be updated before start
    if (this.lastValidEndDate && this.lastValidEndDate <= sameLengthOfStayEndDate.getTime() || this.isNotFirstTimeSameLength) {
      this.resetAndSetStart(date);
    } else {
      this.isNotFirstTimeSameLength = true;
      this.end = sameLengthOfStayEndDate;
      this.start = date;
      this.setCurrentTrigger('end');
    }
  }
  resetAndSetStart(date) {
    this.start = date;
    this.end = undefined;
    this.setCurrentTrigger('end');
  }
  // Helper method to validate if the dates of holiday and calendar are the same (Format of holiday: yyyy-mm-dd)
  isDateHolidayEqual(dateHoliday, dateCalendar) {
    return this.holidaysConfigured && dateHoliday.toDateString() === dateCalendar.toDateString();
  }
  // Helper method to validate if the month (in the same year) of holiday and calendar are the same (Format of holiday: yyyy-mm-dd)
  // Format of monthInfoCalendar ({month, name, year})
  isMonthHolidayEqual(holiday, monthInfoCalendar) {
    if (this.holidaysConfigured) {
      return holiday.getMonth() === monthInfoCalendar.month && holiday.getFullYear() === monthInfoCalendar.year;
    }
    return false;
  }
  // Configure the holidays according to origin and destination
  configureHolidays(holidays) {
    this.dayHolidays = {};
    const holidaysOrigin = holidays.filter(holiday => holiday.origin);
    const holidaysDestination = JSON.parse(JSON.stringify(holidays.filter(holiday => !holiday.origin)));
    const holidaysDestinationFiltered = holidaysDestination.filter(destination => !holidaysOrigin.find(origin => origin.date === destination.date && origin.name === destination.name));
    holidaysDestinationFiltered.forEach(hd => {
      hd.name += ` (${hd.country})`;
    });
    this.holidays = holidaysOrigin.concat(holidaysDestinationFiltered);
    this.holidays.forEach(hd => {
      hd.dateSort = new Date(hd.date.split('-').join('/'));
      hd.dateSort.setHours(0, 0, 0, 0);
      hd.month = hd.dateSort.toLocaleString('default', {
        month: 'short'
      });
      this.dayHolidays[hd.dateSort.toDateString()] = true;
    });
    this.holidays.sort((date1, date2) => date1.dateSort && date2.dateSort && (date1.dateSort.getTime() > date2.dateSort.getTime() || date1.dateSort.getTime() === date2.dateSort.getTime() && date1.origin < date2.origin) ? 1 : -1);
    this.holidaysConfigured = true;
    this.setHolidaysSelection();
  }
  // Helper method to return the holiday names in the dates selected
  setHolidaysSelection() {
    if (this.holidaysConfigured && (this.start || this.end)) {
      const start = new Date(this.start);
      start.setHours(0, 0, 0, 0);
      let end = new Date();
      if (this.end) {
        end = new Date(this.end);
        end.setHours(0, 0, 0, 0);
      }
      this.holidaysName = '';
      for (const holiday of this.holidays) {
        if (this.end && holiday.dateSort && holiday.dateSort >= start && holiday.dateSort <= end || !this.end && holiday.dateSort && holiday.dateSort.getTime() === start.getTime()) {
          this.holidaysName = this.holidaysName === '' ? `${holiday.month} ${holiday.dateSort.getDate()}: ${holiday.name}` : `${this.holidaysName}  •  ${holiday.month} ${holiday.dateSort.getDate()}: ${holiday.name}`;
        }
      }
    } else {
      this.holidaysName = '';
    }
  }
}
const getClassNameList$7 = classNames.bind(styles);
const l10nMonths = {
  monthNames: null
};
class Calendar extends React.Component {
  contentRef;
  isNextDisabled;
  isPrevDisabled;
  _isMounted;
  constructor(props) {
    super(props);
    this.state = {
      months: [],
      renderTimers: []
    };
    if (!l10nMonths.monthNames) {
      l10nMonths.monthNames = this.getMonthNames();
    }
    this.prevMonth = this.prevMonth.bind(this);
    this.nextMonth = this.nextMonth.bind(this);
    this.goToDate = this.goToDate.bind(this);
    this.calculateMonths = this.calculateMonths.bind(this);
    this.calcNewStartDate = this.calcNewStartDate.bind(this);
    this.setDisabledButtons = this.setDisabledButtons.bind(this);
    this.contentRef = React.createRef();
    if (props.holidays && props.holidays.length > 0) {
      props.selection.configureHolidays(props.holidays);
      props.updateDatePicker();
    }
    this.onSelectDate = this.onSelectDate.bind(this);
    this.getHolidayName = this.getHolidayName.bind(this);
  }
  componentDidMount() {
    const {
      monthsToRender
    } = this.props;
    this._isMounted = true;
    window.requestAnimationFrame(() => {
      this.calculateMonths(monthsToRender);
    });
  }
  shouldComponentUpdate(nextProps, nextState) {
    const {
      endDate,
      datePickerContent,
      selectionEnd,
      selectionStart,
      startDate,
      displayDate
    } = this.props;
    const {
      months
    } = this.state;
    if (months !== nextState.months) {
      return true;
    }
    const selectionChanged = !(nextProps.selectionStart === selectionStart && nextProps.selectionEnd === selectionEnd);
    const requiredPropsChanged = startDate !== nextProps.startDate || endDate !== nextProps.endDate || displayDate && displayDate !== nextProps.displayDate || datePickerContent !== nextProps.datePickerContent;
    if (nextProps.displayDate) {
      this.goToDate(nextProps.displayDate);
    }
    return selectionChanged || requiredPropsChanged;
  }
  componentWillUnmount() {
    const {
      renderTimers
    } = this.state;
    this._isMounted = false;
    renderTimers.forEach(timer => {
      clearTimeout(timer);
    });
  }
  onSelectDate(event, date, localizedText) {
    const {
      isDialog,
      onChangeEndDate,
      onChangeStartDate,
      onSelectDate,
      selection,
      singleMode,
      updateDatePicker
    } = this.props;
    const changeStart = selection.isSelectingStart;
    const dateText = date.toDateString();
    if (singleMode) {
      selection.singleDateSelected(date);
    } else {
      selection.dateSelected(date);
    }
    if (selection.isSelectingEnd === true) {
      this.announceDateInstructions(localizedText.selectEndDate(dateText), 'polite');
    } else {
      this.announceDateInstructions(localizedText.selectDone(dateText), 'polite');
    }
    if (isDialog && onSelectDate) {
      // Update state internally in dialog-view
      onSelectDate();
    } else {
      // Update state up the chain in uitk-date-picker (needed for menu)
      updateDatePicker();
    }
    if (onChangeStartDate && (changeStart || selection.start && !selection.end)) {
      onChangeStartDate(selection.start);
    } else if (onChangeEndDate && selection.end) {
      onChangeEndDate(selection.end);
    }
  }
  getHolidayName(date) {
    const {
      l10n,
      selection,
      holidays
    } = this.props;
    if (selection.holidaysConfigured && selection.dayHolidays[date.toDateString()]) {
      let parsedDate;
      let dateString;
      const holidayData = holidays && holidays.find(holiday => {
        parsedDate = l10n.parseLocalDateString(holiday.date, {
          dateFormat: 'iso',
          raw: 'yyyy-MM-dd'
        });
        dateString = new Date(parsedDate).toDateString();
        return dateString === date.toDateString();
      });
      return holidayData?.name;
    }
  }
  getMonthNames() {
    // Use this to get month names programmatically
    const date = new Date();
    const curYear = date.getFullYear();
    const monthNames = [];
    const {
      l10n
    } = this.props;
    for (let i = 0; i < 12; i += 1) {
      monthNames.push(l10n.formatDate(new Date(curYear, i), {
        raw: 'MMMM',
        dateFormat: 'month-full'
      }));
    }
    return monthNames;
  }
  getSelection(monthInfo, selection) {
    const currentMonth = monthInfo.month;
    const currentYear = monthInfo.year;
    const selectedStart = selection.start;
    const selectedEnd = selection.end;
    // Number of Months
    // 12 * year because 12 months in a year
    // plus month number
    // This makes it easier compare if a certain month is before after another month
    const currentNumOfMonths = currentYear * 12 + currentMonth;
    const selectedStartNumOfMonths = selectedStart ? selectedStart.getFullYear() * 12 + selectedStart.getMonth() : false;
    const selectedEndNumOfMonths = selectedEnd ? selectedEnd.getFullYear() * 12 + selectedEnd.getMonth() : false;
    // Does current month contain start date or end date
    if (currentNumOfMonths === selectedStartNumOfMonths || currentNumOfMonths === selectedEndNumOfMonths) {
      return selection;
    }
    // Does current month contain some of the range (and not start or end date)
    if (selectedEndNumOfMonths !== false && currentNumOfMonths > selectedStartNumOfMonths && currentNumOfMonths < selectedEndNumOfMonths) {
      return selection;
    }
    // If current month doesn't contain any selected days then selection object doesn't need to be passed to month
    return undefined;
  }
  setDisabledButtons(months) {
    const {
      props
    } = this;
    const {
      length
    } = months;
    if (months) {
      this.isPrevDisabled = months[0].month === props.startDate.getMonth() && months[0].year === props.startDate.getFullYear();
      this.isNextDisabled = months[length - 1].month === props.endDate.getMonth() && months[length - 1].year === props.endDate.getFullYear();
    }
  }
  nextMonth() {
    const {
      monthsToRender,
      onChangeMonth
    } = this.props;
    const newStartDate = this.calcNewStartDate(true);
    this.calculateMonths(monthsToRender, newStartDate, true, true);
    if (onChangeMonth) {
      onChangeMonth(newStartDate);
    }
  }
  prevMonth() {
    const {
      monthsToRender,
      onChangeMonth
    } = this.props;
    const newStartDate = this.calcNewStartDate(false);
    this.calculateMonths(monthsToRender, newStartDate, false, true);
    if (onChangeMonth) {
      onChangeMonth(newStartDate);
    }
  }
  goToDate(displayDate) {
    const {
      monthsToRender,
      onChangeMonth,
      clearDisplayDate
    } = this.props;
    const {
      months
    } = this.state;
    if (!months.some(({
      year,
      month
    }) => displayDate.getFullYear() === year && displayDate.getMonth() === month)) {
      this.calculateMonths(monthsToRender, displayDate);
      if (onChangeMonth) {
        onChangeMonth(displayDate);
      }
      if (clearDisplayDate) {
        clearDisplayDate();
      }
    }
  }
  calcNewStartDate(isNext) {
    const {
      monthsToRender
    } = this.props;
    const {
      months
    } = this.state;
    const month = months;
    const lastMonth = month.length - 1;
    const totalMonthsToRender = monthsToRender || 2;
    const monthsToSubstract = isNext ? totalMonthsToRender - 2 : monthsToRender;
    return new Date(month[lastMonth].year,
    // @ts-ignore
    month[lastMonth].month - monthsToSubstract, 1, 12);
  }
  scrollToSelection() {
    /* This method uses a non-react way of scrolling to selection,
       because it is the simplest way to do it.
     */
    const {
      selection
    } = this.props;
    const selectedClassNames = getClassNameList$7('uitk-date-picker-day-number', {
      endSelected: selection.isSelectingEnd && selection.end,
      startSelected: selection.isSelectingStart && selection.start
    });
    const selectedDateElemArr = document.getElementsByClassName(selectedClassNames);
    if (selectedDateElemArr.length === 1) {
      // ** Firefox IE edge
      const calendarWrapper = document.querySelector('.uitk-calendar');
      if (calendarWrapper && calendarWrapper.scrollTo) {
        const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
        const toScroll = selectedDateElemArr[0].offsetTop - viewportHeight / 2;
        calendarWrapper.scrollTo(0, toScroll);
      } else if (selectedDateElemArr[0].scrollIntoViewIfNeeded) {
        selectedDateElemArr[0].scrollIntoViewIfNeeded();
      } else if (selectedDateElemArr[0].scrollIntoView) {
        selectedDateElemArr[0].scrollIntoView();
      }
    }
  }
  recursivelyRenderRemainingMonths(months, indexOfLastRenderedMonth) {
    const {
      renderTimers
    } = this.state;
    const indexOfNextMonthToRender = indexOfLastRenderedMonth + 2;
    if (indexOfNextMonthToRender <= months.length + 1) {
      const timer = setTimeout(() => {
        this.setState({
          months: months.slice(0, indexOfNextMonthToRender)
        });
        this.recursivelyRenderRemainingMonths(months, indexOfNextMonthToRender);
      }, 0);
      renderTimers.push(timer);
    }
  }
  calculateMonths(max, newStartDate, nextMonth, keyPressed) {
    const maxCount = max || 48;
    const {
      endDate,
      firstMonth,
      isDialog,
      l10n,
      selection
    } = this.props;
    const monthOne = newStartDate || firstMonth;
    const endDateMonthYear = new Date(endDate?.getFullYear(), endDate?.getMonth());
    const selectionStartDateMonthYear = new Date(selection?.start?.getFullYear(), selection?.start?.getMonth());
    let monthsToSubstract = 0;
    if (endDateMonthYear.getTime() === selectionStartDateMonthYear.getTime() && selection?.start?.getTime() >= new Date().getTime()) {
      monthsToSubstract = 1;
    }
    let currentMonth = new Date(monthOne.getFullYear(), monthOne.getMonth() - monthsToSubstract, 1, 12);
    /*  Building a model for each month by adding 1 month at a time.
        Since bookings are usually not more than 12 months in advance,
        this is acceptable.
      */
    const months = [];
    let index = 0;
    if (endDate) {
      while (endDate.getTime() - currentMonth.getTime() >= 0 && index < maxCount) {
        months.push({
          month: currentMonth.getMonth(),
          name: l10nMonths.monthNames[currentMonth.getMonth()],
          year: currentMonth.getFullYear()
        });
        currentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1, 12);
        index += 1;
      }
      if (isDialog) {
        this.renderUpToSelectedMonth(months);
      } else {
        // No need for recursion on Menu views
        this.setDisabledButtons(months);
        if (this._isMounted) {
          this.setState({
            months
          });
          if (keyPressed) {
            // eslint-disable-next-line max-depth
            if (months[1]) {
              liveAnnounce(l10n.formatText('datePicker.currentMonths.a11yLabel', `${months[0].name} ${months[0].year}`, `${months[1].name} ${months[1].year}`));
            } else {
              liveAnnounce(l10n.formatText('datePicker.currentMonth.a11yLabel', months[0].name));
            }
          }
        }
      }
    }
  }
  announceDateInstructions(message, setting) {
    liveAnnounce(message, setting, undefined, this.contentRef);
  }
  // First render up to the month after the selected month, then recursively render remaining months
  renderUpToSelectedMonth(months) {
    const {
      selectionEnd,
      selectionStart
    } = this.props;
    const selection = selectionEnd || selectionStart;
    let indexOfSelectedMonth = 0;
    if (selection) {
      const selectedMonth = selection.getMonth();
      const selectedYear = selection.getFullYear();
      indexOfSelectedMonth = months.findIndex(m => m.month === selectedMonth && m.year === selectedYear);
      this.setState({
        months: months.slice(0, indexOfSelectedMonth + 2)
      });
      this.scrollToSelection();
    }
    this.recursivelyRenderRemainingMonths(months, indexOfSelectedMonth + 1);
  }
  render() {
    const {
      buttonText,
      className,
      datePickerContent,
      endDate,
      firstDayOfWeek,
      holidays,
      isDialog,
      l10n,
      nextPageAnalytics,
      onChangeEndDate,
      onChangeStartDate,
      onScrollHandler,
      previousPageAnalytics,
      selection,
      singleMode,
      skipToDoneButton,
      startDate,
      updateDatePicker
    } = this.props;
    const {
      months
    } = this.state;
    const monthContainerClasses = getClassNameList$7({
      'uitk-date-picker-menu-months-container': !isDialog
    });
    const calendarClasses = getClassNameList$7('uitk-calendar', className);
    const getHolidayName = holidays && holidays.length > 0 ? this.getHolidayName : undefined;
    return jsxs("div", {
      className: calendarClasses,
      ref: this.contentRef,
      children: [!isDialog && jsxs(EGDSLayoutFlex, {
        className: getClassNameList$7('uitk-date-picker-menu-pagination-container'),
        justifyContent: "space-between",
        children: [jsx(EGDSLayoutFlexItem, {
          children: jsx(EGDSPagingPrevButton, {
            disabled: this.isPrevDisabled,
            iconId: "prevMonth",
            iconTitle: l10n.formatText('datePicker.previousMonth'),
            onClick: this.prevMonth,
            analytics: previousPageAnalytics,
            "data-stid": "date-picker-paging"
          })
        }), jsx(EGDSLayoutFlexItem, {
          children: jsx(EGDSPagingNextButton, {
            disabled: this.isNextDisabled,
            iconId: "nextMonth",
            iconTitle: l10n.formatText('datepicker.nextMonth'),
            onClick: this.nextMonth,
            analytics: nextPageAnalytics,
            "data-stid": "date-picker-paging"
          })
        })]
      }), jsx("div", {
        className: monthContainerClasses,
        children: months.map((monthInfo, index) => jsxs(React.Fragment, {
          children: [createElement(EGDSMonth, {
            ...monthInfo,
            datePickerContent: datePickerContent,
            getHolidayName: getHolidayName,
            minDate: startDate,
            maxDate: endDate,
            key: `${monthInfo.year}-${monthInfo.month}`,
            selection: this.getSelection(monthInfo, selection),
            updateDatePicker: updateDatePicker,
            firstDayOfWeek: firstDayOfWeek,
            onSelectDate: this.onSelectDate,
            singleMode: singleMode,
            onChangeStartDate: onChangeStartDate,
            onChangeEndDate: onChangeEndDate,
            buttonText: buttonText,
            isDialog: isDialog,
            onScrollHandler: onScrollHandler
          }), months.length - 1 > index && skipToDoneButton && skipToDoneButton()]
        }, `${monthInfo.name}-${index}`))
      })]
    });
  }
}
const EGDSCalendar = props => {
  const sharedUIL10n = useLocalization();
  return jsx(Calendar, {
    l10n: sharedUIL10n,
    ...props
  });
};
const EGDSNewCalendar = EGDSCalendar;
const getClassNameList$6 = classNames.bind(styles);
var EGDSDatePickerDateFormat;
(function (EGDSDatePickerDateFormat) {
  EGDSDatePickerDateFormat["YMD"] = "yyyy-MM-dd";
})(EGDSDatePickerDateFormat || (EGDSDatePickerDateFormat = {}));
const mapDatePickerProps = source => ({
  ...source.datePickerProps,
  onChangeEndDate: source.onChangeEndDate,
  onChangeMonth: source.onChangeMonth,
  onChangeStartDate: source.onChangeStartDate,
  onClearDates: source.onClearDates,
  onDismiss: source.onDismiss,
  onScrollHandler: source.onScrollHandler,
  onSubmit: source.onSubmit,
  preventAutoFocus: source.preventAutoFocus,
  singleMode: source.singleMode && !source.useSingleTrigger,
  useClearDates: source.useClearDates,
  useSingleTrigger: source.useSingleTrigger
});
/**
 * Get display values and values for date field
 */
const getFormattedDates = (l10n, dateProps, dateFormat, mediumDateFormatProp) => {
  const mediumDateFormat = {
    dateFormat: 'medium',
    skeleton: mediumDateFormatProp ?? 'MMMd'
  };
  const isoDateFormat = {
    dateFormat: 'iso-date',
    raw: dateFormat
  };
  const {
    value,
    placeholder
  } = dateProps;
  return {
    value: l10n.formatDate(value, isoDateFormat),
    formattedBtnText: value ? l10n.formatDate(value, mediumDateFormat) : placeholder,
    accessibleFormattedText: value ? l10n.formatDate(value, mediumDateFormat) : placeholder,
    label: jsx("span", {
      "aria-hidden": "true",
      children: dateProps.label
    })
  };
};
const CustomTriggerDialog = props => {
  const {
    updateViewport
  } = props;
  useEffect(() => {
    if (updateViewport) {
      updateViewport(true);
    }
  }, []);
  const enhancedDatePickerProps = mapDatePickerProps(props);
  return jsx(EGDSFullscreenDialog, {
    dialogShow: true,
    animation: EGDSDialogAnimationType.SLIDEUP,
    children: jsx(EGDSDatePickerDialogView, {
      ...enhancedDatePickerProps
    })
  });
};
const CustomTriggerMenu = props => {
  const {
    updateViewport
  } = props;
  useEffect(() => {
    if (updateViewport) {
      updateViewport(false);
    }
  }, []);
  const enhancedDatePickerProps = mapDatePickerProps(props);
  return jsx(EGDSDatePickerMenuView, {
    ...enhancedDatePickerProps
  });
};
CustomTriggerDialog.displayName = 'CustomTriggerDialog';
const DialogEndDateField = props => {
  const {
    dateFormat,
    endProps,
    l10n,
    mediumDateFormatProp,
    onChange,
    routerLinkWrapper
  } = props;
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  if (!endProps) {
    return null;
  }
  const {
    value,
    accessibleFormattedText,
    formattedBtnText,
    label
  } = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp);
  const {
    dateType
  } = endProps;
  const buttonLabel = `${endProps.label} ${accessibleFormattedText}`;
  const setContext = () => {
    props.openDatePicker(dateType);
  };
  const textInputClasses = getClassNameList$6({
    'is-hidden': isMounted
  });
  return jsx(EGDSTextInput, {
    id: endProps.id,
    name: endProps.name,
    className: textInputClasses,
    label: label,
    placeholder: endProps.placeholder,
    value: value,
    onChange: onChange,
    "data-stid": "input-date",
    icon: "today",
    invalid: endProps.invalid,
    children: isMounted && routerLinkWrapper && routerLinkWrapper(jsx(EGDSFauxInput, {
      "aria-label": buttonLabel,
      className: getClassNameList$6('uitk-form-field-trigger'),
      domRef: endProps.domRef,
      "data-name": endProps.name,
      id: `${endProps.id}-btn`,
      "data-stid": "open-date-picker",
      type: "button",
      onClick: setContext,
      analytics: endProps.analytics,
      children: formattedBtnText
    }))
  });
};
DialogEndDateField.displayName = 'DialogEndDateField';
const DialogDateFields = props => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    if (props.updateViewport) {
      props.updateViewport(true);
    }
  }, []);
  const {
    dateFieldWrapperClasses,
    dateFormat,
    dialogWrapper,
    l10n,
    mediumDateFormatProp,
    onChange,
    routerLinkWrapper,
    showDatePicker,
    singleMode,
    space,
    stacked,
    startProps,
    endProps,
    triggeredField,
    useSingleTrigger
  } = props;
  const {
    value: startValue,
    formattedBtnText: startFormattedBtnText,
    accessibleFormattedText: startAccessibleFormattedText,
    label: startLabel
  } = getFormattedDates(l10n, startProps, dateFormat, mediumDateFormatProp);
  let value = startValue;
  let formattedBtnText = startFormattedBtnText;
  let buttonLabel = `${startProps.label}`;
  if (startAccessibleFormattedText) {
    buttonLabel += ` ${startAccessibleFormattedText}`;
  }
  if (useSingleTrigger && endProps) {
    const {
      value: endValue,
      formattedBtnText: endFormattedBtnText,
      accessibleFormattedText: endAccessibleFormattedText
    } = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp);
    if (endValue) {
      value += ` - ${endValue}`;
    }
    if (endFormattedBtnText) {
      formattedBtnText += ` - ${endFormattedBtnText}`;
    }
    if (endAccessibleFormattedText) {
      buttonLabel = `${startProps.label} ${l10n.formatText('datepicker.buttonLabel.endAccessibleFormattedText', startAccessibleFormattedText, endAccessibleFormattedText)}`;
    }
  }
  const setContext = () => {
    const {
      dateType
    } = startProps;
    props.openDatePicker(dateType);
  };
  const shouldBeStacked = stacked && !singleMode && !useSingleTrigger;
  const fieldWrapperClasses = getClassNameList$6({
    'uitk-date-field-wrapper': !shouldBeStacked
  });
  const fieldLayoutDirection = shouldBeStacked ? 'column' : 'row';
  const wrapperClasses = getClassNameList$6({
    [`${dateFieldWrapperClasses}`]: dateFieldWrapperClasses
  }, 'uitk-date-fields-wrapper');
  const enhancedDatePickerProps = mapDatePickerProps(props);
  const textInputClasses = getClassNameList$6({
    'is-hidden': isMounted
  });
  return jsx(EGDSLayoutFlexItem, {
    className: wrapperClasses,
    grow: 1,
    children: jsxs(EGDSLayoutFlex, {
      className: getClassNameList$6('uitk-date-fields'),
      direction: fieldLayoutDirection,
      space: space,
      children: [showDatePicker && triggeredField && dialogWrapper(jsx(EGDSFullscreenDialog, {
        dialogShow: true,
        animation: EGDSDialogAnimationType.SLIDEUP,
        children: jsx(EGDSDatePickerDialogView, {
          ...enhancedDatePickerProps
        })
      }), triggeredField), jsx(EGDSLayoutFlexItem, {
        className: fieldWrapperClasses,
        grow: 1,
        minWidth: "zero",
        maxWidth: singleMode || useSingleTrigger || stacked ? undefined : 'half_width',
        children: jsx("div", {
          children: jsx(EGDSTextInput, {
            id: startProps.id,
            name: startProps.name,
            className: textInputClasses,
            label: startLabel,
            placeholder: startProps.placeholder,
            value: value,
            onChange: onChange,
            "data-stid": "input-date",
            icon: "today",
            invalid: startProps.invalid,
            children: isMounted && routerLinkWrapper && routerLinkWrapper(jsx(EGDSFauxInput, {
              "aria-label": buttonLabel,
              className: getClassNameList$6('uitk-form-field-trigger'),
              domRef: startProps.domRef,
              "data-name": startProps.name,
              id: `${startProps.id}-btn`,
              "data-stid": "open-date-picker",
              type: "button",
              onClick: setContext,
              analytics: startProps.analytics,
              children: formattedBtnText
            }))
          })
        })
      }), !singleMode && !useSingleTrigger && jsx(EGDSLayoutFlexItem, {
        className: fieldWrapperClasses,
        grow: 1,
        minWidth: "zero",
        maxWidth: stacked ? undefined : 'half_width',
        children: jsx("div", {
          children: jsx(DialogEndDateField, {
            ...props
          })
        })
      })]
    })
  });
};
DialogDateFields.displayName = 'DialogDateFields';
const MenuEndDateField = props => {
  const {
    dateFormat,
    mediumDateFormatProp,
    endProps,
    l10n,
    onChange,
    onSubmit,
    showDatePicker,
    triggeredField
  } = props;
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  if (!endProps) {
    return null;
  }
  const {
    value,
    formattedBtnText,
    accessibleFormattedText,
    label
  } = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp);
  const {
    dateType
  } = endProps;
  const buttonLabel = `${endProps.label} ${accessibleFormattedText}`;
  const setContext = () => {
    props.openDatePicker(dateType);
  };
  // Make sure that both menus aren't opened at the same time on auto-open
  const isOpen = isMounted && showDatePicker;
  const containerClasses = getClassNameList$6('uitk-date-picker-menu-container', {
    'uitk-date-picker-menu-container-single': !endProps.doubleMonths,
    'uitk-date-picker-menu-container-double': endProps.doubleMonths
  });
  const enhancedDatePickerProps = mapDatePickerProps(props);
  const textInputClasses = getClassNameList$6({
    'is-hidden': isMounted
  });
  return jsx(EGDSContextProvider, {
    config: {
      context: 'egds-date-picker-context'
    },
    children: jsxs(EGDSMenu, {
      isOpen: isOpen,
      className: getClassNameList$6('uitk-date-picker-menu'),
      onDismiss: onSubmit,
      children: [jsx(EGDSMenuTrigger, {
        children: jsx(EGDSTextInput, {
          className: textInputClasses,
          id: endProps.id,
          name: endProps.name,
          label: label,
          placeholder: endProps.placeholder,
          value: value,
          onChange: onChange,
          "data-stid": "input-date",
          icon: "today",
          invalid: endProps.invalid,
          children: isMounted && jsx(EGDSFauxInput, {
            "aria-label": buttonLabel,
            className: getClassNameList$6('uitk-form-field-trigger'),
            domRef: endProps.domRef,
            "data-name": endProps.name,
            id: `${endProps.id}-btn`,
            "data-stid": "open-date-picker",
            type: "button",
            onClick: setContext,
            analytics: endProps.analytics,
            children: formattedBtnText
          })
        })
      }), showDatePicker && triggeredField === 'end' && jsx(EGDSMenuContainer, {
        position: PositionType.LEFT,
        className: containerClasses,
        autoPosition: true,
        children: jsx(EGDSDatePickerMenuView, {
          ...enhancedDatePickerProps
        })
      })]
    })
  });
};
MenuEndDateField.displayName = 'MenuEndDateField';
const MenuDateFields = props => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    if (props.updateViewport) {
      props.updateViewport(false);
    }
  }, []);
  const {
    dateFieldWrapperClasses,
    dateFormat,
    l10n,
    mediumDateFormatProp,
    onChange,
    onSubmit,
    showDatePicker,
    singleMode,
    space,
    stacked,
    startProps,
    endProps,
    triggeredField,
    useSingleTrigger
  } = props;
  const {
    value: startValue,
    formattedBtnText: startFormattedBtnText,
    accessibleFormattedText: startAccessibleFormattedText,
    label: startLabel
  } = getFormattedDates(l10n, startProps, dateFormat, mediumDateFormatProp);
  let value = startValue;
  let formattedBtnText = startFormattedBtnText;
  let buttonLabel = `${startProps.label}`;
  if (startAccessibleFormattedText) {
    buttonLabel += ` ${startAccessibleFormattedText}`;
  }
  if (useSingleTrigger && endProps) {
    const {
      value: endValue,
      formattedBtnText: endFormattedBtnText,
      accessibleFormattedText: endAccessibleFormattedText
    } = getFormattedDates(l10n, endProps, dateFormat, mediumDateFormatProp);
    if (endValue) {
      value += ` - ${endValue}`;
    }
    if (endFormattedBtnText) {
      formattedBtnText += ` - ${endFormattedBtnText}`;
    }
    if (endAccessibleFormattedText) {
      buttonLabel = `${startProps.label} ${l10n.formatText('datepicker.buttonLabel.endAccessibleFormattedText', startAccessibleFormattedText, endAccessibleFormattedText)}`;
    }
  }
  const setContext = () => {
    props.openDatePicker(startProps.dateType);
  };
  const isOpen = showDatePicker;
  const containerClasses = getClassNameList$6('uitk-date-picker-menu-container', {
    'uitk-date-picker-menu-container-single': !startProps.doubleMonths,
    'uitk-date-picker-menu-container-double': startProps.doubleMonths
  });
  const enhancedDatePickerProps = mapDatePickerProps(props);
  const shouldBeStacked = stacked && !singleMode && !useSingleTrigger;
  const fieldWrapperClasses = getClassNameList$6({
    'uitk-date-field-wrapper': !shouldBeStacked
  });
  const fieldLayoutDirection = shouldBeStacked ? 'column' : 'row';
  const wrapperClasses = getClassNameList$6({
    [`${dateFieldWrapperClasses}`]: dateFieldWrapperClasses
  }, 'uitk-date-fields-wrapper');
  const textInputClasses = getClassNameList$6({
    'is-hidden': isMounted
  });
  return jsx(EGDSLayoutFlexItem, {
    className: wrapperClasses,
    grow: 1,
    children: jsxs(EGDSLayoutFlex, {
      className: getClassNameList$6('uitk-date-fields'),
      space: space,
      direction: fieldLayoutDirection,
      children: [jsx(EGDSLayoutFlexItem, {
        className: fieldWrapperClasses,
        grow: 1,
        minWidth: "zero",
        maxWidth: singleMode || useSingleTrigger || stacked ? undefined : 'half_width',
        children: jsx("div", {
          children: jsx(EGDSContextProvider, {
            config: {
              context: 'egds-date-picker-context'
            },
            children: jsxs(EGDSMenu, {
              isOpen: isOpen,
              className: getClassNameList$6('uitk-date-picker-menu'),
              onDismiss: onSubmit,
              children: [jsx(EGDSMenuTrigger, {
                children: jsx(EGDSTextInput, {
                  className: textInputClasses,
                  id: startProps.id,
                  name: startProps.name,
                  label: startLabel,
                  placeholder: startProps.placeholder,
                  value: value,
                  onChange: onChange,
                  "data-stid": "input-date",
                  icon: "today",
                  invalid: startProps.invalid,
                  children: isMounted && jsx(EGDSFauxInput, {
                    "aria-label": buttonLabel,
                    className: getClassNameList$6('uitk-form-field-trigger'),
                    domRef: startProps.domRef,
                    "data-name": startProps.name,
                    id: `${startProps.id}-btn`,
                    "data-stid": "open-date-picker",
                    type: "button",
                    onClick: setContext,
                    analytics: startProps.analytics,
                    children: formattedBtnText
                  })
                })
              }), showDatePicker && triggeredField === 'start' && jsx(EGDSMenuContainer, {
                position: PositionType.LEFT,
                className: containerClasses,
                autoPosition: true,
                children: jsx(EGDSDatePickerMenuView, {
                  ...enhancedDatePickerProps
                })
              })]
            })
          })
        })
      }), !singleMode && !useSingleTrigger && jsx(EGDSLayoutFlexItem, {
        className: fieldWrapperClasses,
        grow: 1,
        minWidth: "zero",
        maxWidth: stacked ? undefined : 'half_width',
        children: jsx("div", {
          children: jsx(MenuEndDateField, {
            ...props
          })
        })
      })]
    })
  });
};
MenuDateFields.displayName = 'MenuDateFields';
class DateField extends React.Component {
  static displayName = 'EGDSDateField';
  static defaultProps = {
    dateFormat: EGDSDatePickerDateFormat.YMD,
    space: 'three',
    triggeredField: 'start'
  };
  render() {
    const {
      useCustomTrigger,
      datePickerProps
    } = this.props;
    if (useCustomTrigger) {
      switch (datePickerProps.renderType) {
        case EGDSDatePickerRenderType.DIALOG:
          return jsx(CustomTriggerDialog, {
            ...this.props
          });
        case EGDSDatePickerRenderType.MENU:
          return jsx(CustomTriggerMenu, {
            ...this.props
          });
        default:
          return jsxs(Viewport, {
            children: [jsx(ViewSmall, {
              children: jsx(CustomTriggerDialog, {
                ...this.props
              })
            }), jsx(ViewLarge, {
              children: jsx(CustomTriggerMenu, {
                ...this.props
              })
            })]
          });
      }
    }
    switch (datePickerProps.renderType) {
      case EGDSDatePickerRenderType.DIALOG:
        return jsx(DialogDateFields, {
          ...this.props
        });
      case EGDSDatePickerRenderType.MENU:
        return jsx(MenuDateFields, {
          ...this.props
        });
      default:
        return jsxs(Viewport, {
          children: [jsx(ViewSmall, {
            children: jsx(DialogDateFields, {
              ...this.props
            })
          }), jsx(ViewLarge, {
            children: jsx(MenuDateFields, {
              ...this.props
            })
          })]
        });
    }
  }
}
const EGDSDateField = props => {
  const l10n = useLocalization();
  return jsx(DateField, {
    l10n: l10n,
    ...props
  });
};
EGDSDateField.displayName = 'EGDSDateField';
const EGDSNewDateField = EGDSDateField;
const getClassNameList$5 = classNames.bind(styles);
var EGDSDatePickerTriggeredSelector;
(function (EGDSDatePickerTriggeredSelector) {
  EGDSDatePickerTriggeredSelector["START"] = "start";
  EGDSDatePickerTriggeredSelector["END"] = "end";
  EGDSDatePickerTriggeredSelector["NONE"] = "none";
})(EGDSDatePickerTriggeredSelector || (EGDSDatePickerTriggeredSelector = {}));
var EGDSDatePickerRenderType;
(function (EGDSDatePickerRenderType) {
  EGDSDatePickerRenderType["DIALOG"] = "dialog";
  EGDSDatePickerRenderType["MENU"] = "menu";
})(EGDSDatePickerRenderType || (EGDSDatePickerRenderType = {}));
class DatePicker extends React.Component {
  static defaultProps = {
    daysBookableInAdvance: 500,
    midnightBookingOffset: 4,
    preventAutoFocus: false,
    singleMode: false
  };
  today;
  tomorrow;
  preselectedEnd;
  preselectedStart;
  startDomRef;
  endDomRef;
  lastValidDate;
  firstValidDate;
  isDialog;
  isOpenInternallyDriven;
  constructor(props) {
    super(props);
    const initialValues = this.setInitialDates();
    const selection = new DatePickerModel(initialValues.lastValidDate, props.singleDateSelect);
    let name;
    if (props.triggeredSelector) {
      name = props.triggeredSelector;
    } else {
      name = props.dateFieldProps.showDatePicker ? EGDSDatePickerTriggeredSelector.START : '';
    }
    this.startDomRef = props.startDateRef ? props.startDateRef : React.createRef();
    this.endDomRef = props.endDateRef ? props.endDateRef : React.createRef();
    this.state = {
      localizedText: {
        checkInInstructions: () => props.l10n.formatText('datePicker.fromDateInstructions.a11yLabel'),
        checkOutInstructions: () => props.l10n.formatText('datePicker.toDateInstructions.a11yLabel'),
        dateFieldSelected: () => props.l10n.formatText('datePicker.selectedDateField.allyLabel'),
        doneHeader: () => props.l10n.formatText('datePicker.doneHeader'),
        clearHeader: () => props.l10n.formatText('datePicker.clearHeader'),
        selectEndDate: () => props.l10n.formatText('datePicker.dateInput.endDate.placeholder'),
        selectStartDate: () => props.l10n.formatText('datePicker.dateInput.startDate.placeholder'),
        clearedDates: () => props.l10n.formatText('datepicker.cleared.dates')
      },
      name,
      selection
    };
    this.getDatePickerProps = this.getDatePickerProps.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClearDates = this.onClearDates.bind(this);
    this.onDismiss = this.onDismiss.bind(this);
    this.onSelectStartMode = this.onSelectStartMode.bind(this);
    this.onSelectEndMode = this.onSelectEndMode.bind(this);
    this.openDatePicker = this.openDatePicker.bind(this);
    this.selectionClassNames = this.selectionClassNames.bind(this);
    this.setInitialDates = this.setInitialDates.bind(this);
    this.updateDatePicker = this.updateDatePicker.bind(this);
    this.updateViewport = this.updateViewport.bind(this);
  }
  UNSAFE_componentWillMount() {
    this.updateStateWithProps(this.props);
  }
  componentDidMount() {
    const {
      props
    } = this;
    if (props.onMountCB) {
      props.onMountCB(this.startDomRef, this.endDomRef);
    }
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      startDate,
      endDate,
      validStartDate
    } = this.props;
    if (nextProps.startDate !== startDate || nextProps.endDate !== endDate) {
      // PRESELECT DATES
      this.preselectedStart = nextProps.startDate;
      this.preselectedEnd = nextProps.endDate;
    }
    if (nextProps.validStartDate !== validStartDate) {
      this.setFirstValidDate(nextProps);
    }
    this.updateStateWithProps(nextProps);
  }
  componentDidUpdate(prevProps) {
    const {
      dateFieldProps,
      daysBookableInAdvance
    } = this.props;
    const {
      selection
    } = this.state;
    if (prevProps.daysBookableInAdvance !== daysBookableInAdvance) {
      this.lastValidDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + daysBookableInAdvance, this.today.getDate());
      /* This is not the proper way to set state, but given the architecture
         that the selection state property has, is not possible to use setState
         in the selection property. If we try to use setState to update the property
         we will need to use spread operator for keeping the previous values when updating,
         and doing that makes the selection lose the reference for the class DatePickerSelectionState.
         This leads to errors when trying to access to the public methods of that class.
        */
      selection.lastValidEndDate = this.lastValidDate;
    }
    if (!prevProps.dateFieldProps.showDatePicker && dateFieldProps.showDatePicker) {
      if (this.isOpenInternallyDriven) {
        // Reset flag
        this.isOpenInternallyDriven = undefined;
      } else {
        // Client driven show date picker
        this.openDatePicker(EGDSDatePickerTriggeredSelector.START, false);
      }
    }
  }
  onSubmit() {
    const {
      l10n,
      onSubmitCB
    } = this.props;
    const {
      name,
      selection
    } = this.state;
    const {
      start,
      end
    } = selection;
    this.resetLiveAnnounce();
    this.updateDatePicker();
    // PRESELECT DATES
    this.preselectedEnd = l10n.formatDate(end, {
      dateFormat: 'iso-date',
      raw: 'yyyy-MM-dd'
    });
    this.preselectedStart = l10n.formatDate(start, {
      dateFormat: 'iso-date',
      raw: 'yyyy-MM-dd'
    });
    this.focusOnTrigger(name);
    // Submits and closes the datepicker
    if (onSubmitCB) {
      onSubmitCB(start, end, this.isDialog);
    }
  }
  onClearDates() {
    const {
      onClearDatesCB
    } = this.props;
    this.clearDatePicker();
    if (onClearDatesCB) {
      onClearDatesCB();
    }
  }
  onDismiss(noUpdate) {
    const {
      onDismissCB
    } = this.props;
    const {
      name,
      selection
    } = this.state;
    selection.start = null;
    selection.end = null;
    this.updateDatePicker();
    this.resetLiveAnnounce();
    this.focusOnTrigger(name);
    if (onDismissCB) {
      onDismissCB(this.isDialog, noUpdate);
    }
  }
  onSelectStartMode(noUpdate) {
    const {
      onSelectStartModeCB
    } = this.props;
    const {
      localizedText,
      selection
    } = this.state;
    selection.setCurrentTrigger('start');
    liveAnnounce(localizedText.checkInInstructions(), Level.POLITE);
    onSelectStartModeCB && onSelectStartModeCB();
    if (!noUpdate) {
      this.updateDatePicker();
    }
  }
  onSelectEndMode(noUpdate) {
    const {
      onSelectEndModeCB
    } = this.props;
    const {
      localizedText,
      selection
    } = this.state;
    selection.setCurrentTrigger('end');
    liveAnnounce(localizedText.checkOutInstructions(), Level.POLITE);
    onSelectEndModeCB && onSelectEndModeCB();
    if (!noUpdate) {
      this.updateDatePicker();
    }
  }
  getDatePickerProps() {
    const {
      analytics,
      buttonText,
      clearButtonText,
      dateFieldProps,
      datePickerContent,
      doubleMonths,
      endSelectionPlaceholder,
      firstDayOfWeek,
      footerText,
      holidays,
      l10n,
      onDoneButtonClickCB,
      renderType,
      singleMode,
      startSelectionPlaceholder,
      submitDisabled
    } = this.props;
    const {
      localizedText,
      selection
    } = this.state;
    const startSelection = selection.start;
    const endSelection = selection.end;
    const placeholderStartSelection = startSelectionPlaceholder || localizedText.selectStartDate();
    const placeholderEndSelection = endSelectionPlaceholder || localizedText.selectEndDate();
    const formattedStartSelection = startSelection ? l10n.formatDate(startSelection, {
      dateFormat: 'full',
      skeleton: 'MMMEd'
    }) : placeholderStartSelection;
    const formattedEndSelection = endSelection ? l10n.formatDate(endSelection, {
      dateFormat: 'full',
      skeleton: 'MMMEd'
    }) : placeholderEndSelection;
    const datePickerAnalytics = analytics || {};
    const {
      closeAnalytics,
      doneAnalytics,
      nextPageAnalytics,
      previousPageAnalytics
    } = datePickerAnalytics;
    const doneButtonText = buttonText || localizedText.doneHeader();
    const clearDatesButtonText = clearButtonText || localizedText.clearHeader();
    const startSelectedMsg = this.isSelected(true) ? localizedText.dateFieldSelected() : '';
    const endSelectedMsg = this.isSelected(false) ? localizedText.dateFieldSelected() : '';
    return {
      buttonText: doneButtonText,
      clearButtonText: clearDatesButtonText,
      closeAnalytics,
      datePickerContent,
      doneAnalytics,
      doubleMonths,
      endSelectedMsg,
      firstDayOfWeek,
      firstValidDate: this.firstValidDate,
      footerText,
      formattedEndSelection,
      formattedStartSelection,
      holidays,
      labels: {
        end: dateFieldProps.endLabel,
        start: dateFieldProps.startLabel
      },
      lastValidDate: this.lastValidDate,
      nextPageAnalytics,
      onDoneButtonClickCB,
      onSelectEndMode: this.onSelectEndMode,
      onSelectStartMode: this.onSelectStartMode,
      previousPageAnalytics,
      renderType,
      selection,
      selectionClassNames: this.selectionClassNames,
      singleMode,
      startSelectedMsg,
      submitDisabled,
      today: this.today,
      updateDatePicker: this.updateDatePicker
    };
  }
  setFirstValidDate(props) {
    this.firstValidDate = this.today;
    if (props.validStartDate) {
      try {
        this.firstValidDate = props.l10n.parseLocalDateString(props.validStartDate, {
          dateFormat: 'iso',
          raw: 'yyyy-MM-dd'
        });
      } catch (error) {
        console.error(error);
      }
      if (this.firstValidDate < this.today && !props.allowPastDateSearch) {
        this.firstValidDate = this.today;
      }
    }
  }
  setInitialDates() {
    const {
      cutoffDate,
      daysBookableInAdvance,
      endDate,
      l10n,
      midnightBookingOffset,
      singleMode,
      startDate
    } = this.props;
    this.today = new Date();
    this.today.setHours(12);
    this.today.setMinutes(0);
    this.today.setSeconds(0);
    this.today.setMilliseconds(0);
    this.lastValidDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + daysBookableInAdvance);
    if (cutoffDate) {
      try {
        this.lastValidDate = l10n.parseLocalDateString(cutoffDate, {
          dateFormat: 'iso',
          raw: 'yyyy-MM-dd'
        });
      } catch (error) {
        console.error(error);
      }
    }
    this.setFirstValidDate(this.props);
    this.tomorrow = new Date();
    this.tomorrow.setDate(this.firstValidDate.getDate() + 1);
    // Enabled previous date for midnight to 4am (default) bookings
    // This is to allow customers to book late night so they can check-in immediately.
    if (midnightBookingOffset) {
      const now = new Date();
      if (now.getHours() < midnightBookingOffset) {
        this.today.setDate(this.today.getDate() - 1);
      }
    }
    if (endDate && !singleMode) {
      this.preselectedEnd = endDate;
    }
    if (startDate) {
      this.preselectedStart = startDate;
    }
    return {
      firstValidDate: this.firstValidDate,
      lastValidDate: this.lastValidDate
    };
  }
  updateViewport(isDialog) {
    this.isDialog = isDialog;
  }
  updateStateWithProps(props) {
    const {
      l10n,
      singleMode,
      triggeredSelector
    } = props;
    const {
      selection
    } = this.state;
    if (this.preselectedStart) {
      let start = this.firstValidDate;
      try {
        start = l10n.parseLocalDateString(this.preselectedStart, {
          dateFormat: 'iso',
          raw: 'yyyy-MM-dd'
        });
      } catch (error) {
        console.error(error);
      }
      start.setHours(12);
      if (start >= this.firstValidDate) {
        selection.start = start;
      } else {
        this.clearDatePicker();
        this.preselectedStart = null;
        this.preselectedEnd = null;
      }
    }
    if (this.preselectedEnd) {
      let end = this.tomorrow;
      try {
        end = l10n.parseLocalDateString(this.preselectedEnd, {
          dateFormat: 'iso',
          raw: 'yyyy-MM-dd'
        });
      } catch (error) {
        console.error(error);
      }
      end.setHours(12);
      selection.end = end;
    }
    if (!this.preselectedStart && !this.preselectedEnd) {
      selection.start = null;
      selection.end = null;
    }
    // Maybe breaking
    singleMode && delete selection.end;
    if (triggeredSelector) {
      selection.setCurrentTrigger(triggeredSelector);
    }
  }
  resetLiveAnnounce() {
    liveAnnounce('', Level.POLITE);
  }
  openDatePicker(name, isOpenInternallyDriven = true) {
    const {
      props
    } = this;
    this.isOpenInternallyDriven = isOpenInternallyDriven;
    if (name === 'start') {
      this.onSelectStartMode(true);
    } else {
      this.onSelectEndMode(true);
    }
    this.setState({
      name
    });
    if (props.onOpenCB) {
      props.onOpenCB(name, this.isDialog);
    }
  }
  isSelected(isStart) {
    const {
      selection
    } = this.state;
    return isStart && selection.isSelectingStart || !isStart && selection.isSelectingEnd || isStart && !selection.isSelectingStart && !selection.isSelectingEnd;
  }
  selectionClassNames(isStart) {
    return getClassNameList$5('uitk-date-picker-top-bar-selection', {
      selected: this.isSelected(isStart)
    });
  }
  createCustomEvent(value, onChangeEvent) {
    const event = new CustomEvent('change');
    const input = document.createElement('input');
    input.value = value;
    input.addEventListener('change', inputEvent => {
      onChangeEvent(inputEvent);
    });
    input.dispatchEvent(event);
  }
  updateDatePicker() {
    const {
      localizedText,
      selection
    } = this.state;
    const {
      onStartDateResultSelection,
      onEndDateResultSelection
    } = this.props;
    if (onStartDateResultSelection) {
      this.createCustomEvent(selection.start, onStartDateResultSelection);
    }
    if (onEndDateResultSelection) {
      this.createCustomEvent(selection.end, onEndDateResultSelection);
    }
    this.setState({
      localizedText,
      selection
    });
  }
  clearDatePicker() {
    const {
      localizedText,
      selection
    } = this.state;
    selection.start = null;
    selection.end = null;
    liveAnnounce(localizedText.clearedDates());
    this.updateDatePicker();
  }
  focusOnTrigger(name) {
    const focusRef = name === 'start' ? this.startDomRef : this.endDomRef;
    if (isElementVisible(focusRef.current)) {
      focusRef?.current?.focus();
    }
  }
  render() {
    // Const { props } = this;
    const {
      dialogWrapper,
      dateFieldProps,
      doubleMonths,
      onChangeEndDateCB,
      onChangeMonthCB,
      onChangeStartDateCB,
      onScrollHandler,
      preventAutoFocus,
      singleMode,
      triggeredSelector,
      useClearDates,
      useSingleTrigger
    } = this.props;
    const {
      name,
      selection
    } = this.state;
    const {
      dateFieldWrapperClasses,
      dateFormat,
      endAnalytics,
      endId,
      endInvalid,
      endLabel,
      endName,
      endPlaceholder,
      mediumDateFormatProp,
      onChange,
      useCustomTrigger,
      routerLinkWrapper,
      showDatePicker,
      space,
      stacked,
      startAnalytics,
      startId,
      startInvalid,
      startLabel,
      startName,
      startPlaceholder
    } = dateFieldProps;
    let endProps;
    let _dialogWrapper;
    if (dialogWrapper) {
      _dialogWrapper = dialogWrapper;
    } else {
      _dialogWrapper = children => jsx("div", {
        id: "date-picker",
        className: getClassNameList$5('uitk-dialog-layer', 'uitk-dialog-layer-responsive'),
        children: children
      });
    }
    const propsForDateField = {
      dateFieldWrapperClasses,
      dateFormat,
      dialogWrapper: _dialogWrapper,
      mediumDateFormatProp,
      onChange,
      onChangeEndDate: onChangeEndDateCB,
      onChangeMonth: onChangeMonthCB,
      onChangeStartDate: onChangeStartDateCB,
      onClearDates: this.onClearDates,
      onDismiss: this.onDismiss,
      onScrollHandler,
      onSubmit: this.onSubmit,
      openDatePicker: this.openDatePicker,
      preventAutoFocus,
      routerLinkWrapper,
      showDatePicker,
      singleMode,
      space,
      stacked,
      triggeredField: triggeredSelector || name,
      useCustomTrigger,
      useClearDates,
      useSingleTrigger
    };
    const datePickerProps = this.getDatePickerProps();
    const startProps = {
      analytics: startAnalytics,
      dateType: EGDSDatePickerTriggeredSelector.START,
      domRef: this.startDomRef,
      doubleMonths,
      id: startId,
      invalid: startInvalid,
      label: startLabel,
      name: startName || 'startDate',
      placeholder: startPlaceholder,
      value: selection.start
    };
    const allProps = {
      datePickerProps,
      endProps,
      startProps,
      updateViewport: this.updateViewport,
      ...propsForDateField
    };
    if (!singleMode) {
      endProps = {
        analytics: endAnalytics,
        dateType: EGDSDatePickerTriggeredSelector.END,
        domRef: this.endDomRef,
        doubleMonths,
        id: endId,
        invalid: endInvalid,
        label: endLabel,
        name: endName || 'endDate',
        placeholder: endPlaceholder,
        value: selection.end
      };
      allProps.endProps = endProps;
    }
    return jsx(EGDSDateField, {
      ...allProps
    });
  }
}
const EGDSDatePicker = props => {
  const l10n = useLocalization();
  return jsx(DatePicker, {
    l10n: l10n,
    ...props
  });
};
EGDSDatePicker.displayName = 'EGDSDatePicker';
const EGDSNewDatePicker = EGDSDatePicker;
const getClassNameList$4 = classNames.bind(styles);
var DayName;
(function (DayName) {
  DayName[DayName["SUN"] = 0] = "SUN";
  DayName[DayName["MON"] = 1] = "MON";
  DayName[DayName["TUE"] = 2] = "TUE";
  DayName[DayName["WED"] = 3] = "WED";
  DayName[DayName["THU"] = 4] = "THU";
  DayName[DayName["FRI"] = 5] = "FRI";
  DayName[DayName["SAT"] = 6] = "SAT";
})(DayName || (DayName = {}));
const EGDSDay = props => {
  const {
    disabled,
    date,
    selection,
    selectionStart,
    selectionEnd,
    content,
    onSelectDate,
    formattedDate,
    formattedFriendlyDate,
    holidayName
  } = props;
  const l10n = useLocalization();
  let dayContent;
  let dayAriaLabel;
  let dayContentClass;
  let a11yText;
  // Type guard to support both objects and strings as content.
  const isContentObject = arg => typeof arg === 'object' && arg !== null;
  if (isContentObject(content)) {
    dayContent = content.text;
    dayAriaLabel = content.ariaLabel;
    dayContentClass = content.className;
  } else {
    dayContent = content;
  }
  const onClickAction = e => {
    onSelectDate(e, date);
  };
  if (disabled) {
    a11yText = l10n && l10n.formatText('datePicker.dateDisabled.a11yLabel', formattedFriendlyDate || '');
  } else if (selectionStart && selectionStart.getTime() === date.getTime()) {
    a11yText = l10n && l10n.formatText('datePicker.checkInDate.a11yLabel', formattedFriendlyDate || '');
  } else if (selectionEnd && selectionEnd.getTime() === date.getTime()) {
    a11yText = l10n && l10n.formatText('datePicker.checkOutDate.a11yLabel', formattedFriendlyDate || '');
  } else {
    a11yText = `${formattedFriendlyDate}`;
  }
  const a11yDateLabelArr = [a11yText];
  if (holidayName) {
    a11yDateLabelArr.push(holidayName);
  }
  if (dayContent) {
    a11yDateLabelArr.push(dayContent);
  }
  if (dayAriaLabel) {
    a11yDateLabelArr.push(dayAriaLabel);
  }
  const a11yDateLabel = a11yDateLabelArr.join('. ');
  const dayBtnClassNames = getClassNameList$4('uitk-date-picker-day', dayContentClass, {
    'is-disabled': disabled
  }, {
    selected: selection && selection.isDateSelected(date)
  }, {
    edge: selectionStart && selectionStart.getTime() === date.getTime() || selectionEnd && selectionEnd.getTime() === date.getTime()
  });
  return jsx("button", {
    type: "button",
    className: dayBtnClassNames,
    onClick: onClickAction,
    "data-day": formattedDate,
    "data-day-content": dayContent,
    "aria-label": a11yDateLabel,
    disabled: disabled,
    children: !!holidayName && jsx("span", {
      className: getClassNameList$4('uitk-date-picker-day-holiday')
    })
  });
};
const EGDSNewDay = EGDSDay;
const getClassNameList$3 = classNames.bind(styles);
class Month extends React.Component {
  firstDayOfWeekOffset;
  static defaultProps = {
    firstDayOfWeek: DayName.SUN
  };
  monthRef;
  date = new Date(this.props.year, this.props.month, 1, 12);
  constructor(props) {
    super(props);
    this.monthRef = React.createRef();
    this.state = {
      localizedText: {
        selectDone: dateText => props.l10n.formatText('datePicker.selectDone.liveAnnounce', dateText, props.buttonText),
        selectEndDate: dateText => props.l10n.formatText('datePicker.selectEndDate.liveAnnounce', dateText)
      }
    };
    this.firstDayOfWeekOffset = this.getFirstDayOfWeekOffset(props.firstDayOfWeek);
    this.onSelectDate = this.onSelectDate.bind(this);
  }
  componentDidMount() {
    const {
      isDialog,
      onScrollHandler
    } = this.props;
    if (isDialog && onScrollHandler) {
      const today = new Date();
      const date = this.date < today ? today : this.date;
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 1
      };
      const observer = new IntersectionObserver(([entry]) => {
        if (onScrollHandler && entry.intersectionRatio === 1) {
          onScrollHandler(date);
        }
      }, options);
      if (this.monthRef.current) {
        observer.observe(this.monthRef.current);
      }
    }
  }
  shouldComponentUpdate(nextProps) {
    const {
      datePickerContent,
      selection,
      maxDate,
      minDate
    } = this.props;
    const {
      selection: nextSelection,
      maxDate: nextMaxDate,
      minDate: nextMinDate
    } = nextProps;
    // If available dates have changed
    if (maxDate !== nextMaxDate || minDate !== nextMinDate) {
      return true;
    }
    // If Selection has changed
    if (nextSelection) {
      return true;
    }
    if (!!nextSelection !== !!selection) {
      return true;
    }
    // If datePickerContent has changed
    if (nextProps.datePickerContent !== datePickerContent) {
      return true;
    }
    return false;
  }
  onSelectDate(event, date) {
    const {
      onSelectDate
    } = this.props;
    const {
      localizedText
    } = this.state;
    if (onSelectDate) {
      onSelectDate(event, date, localizedText);
    }
  }
  getFirstDayOfWeekOffset(day) {
    if (Object.values(DayName).indexOf(day) === -1 && day !== undefined) {
      console.error("Invalid argument provided to UITK DatePicker's firstDayOfWeek prop. Utilize Day enum from module.");
    }
    const offset = day || 0;
    return offset;
  }
  generateClassNames(date) {
    const {
      selection
    } = this.props;
    const selectionStart = selection ? selection.start : undefined;
    const selectionEnd = selection ? selection.end : undefined;
    const endSelected = selectionEnd && selectionEnd.getTime() === date.getTime();
    const startSelected = selectionStart && selectionStart.getTime() === date.getTime();
    const isFirstDate = date.getDate() === 1;
    const isLastDate = date.getDate() >= 28 && date.getDate() === new Date(date.getYear(), date.getMonth() + 1, 0).getDate();
    const cssClassNames = getClassNameList$3('uitk-date-picker-day-number', {
      end: endSelected && selectionStart,
      endSelected,
      start: startSelected && selectionEnd,
      startSelected,
      'uitk-date-picker-first-of-month': isFirstDate,
      'uitk-date-picker-last-of-month': isLastDate
    });
    return cssClassNames;
  }
  renderDays() {
    const today = new Date();
    const days = [];
    for (let i = 0; i < 7; i++) {
      const {
        l10n,
        month
      } = this.props;
      const dayOfWeekDate = new Date(today.setDate(today.getDate() - today.getDay() + i + this.firstDayOfWeekOffset));
      const dayName = l10n.formatDate(dayOfWeekDate, {
        dateFormat: 'day-of-week-full',
        raw: 'EEEE'
      });
      const dayInitial = l10n.formatDate(dayOfWeekDate, {
        dateFormat: 'day-of-week-short',
        raw: 'EEEEE'
      });
      days.push(jsxs("th", {
        className: getClassNameList$3('uitk-date-picker-day-name'),
        scope: "col",
        children: [jsx("span", {
          className: "is-visually-hidden",
          children: dayName
        }), jsx("span", {
          "aria-hidden": "true",
          children: dayInitial
        })]
      }, `${month}-weekday${dayName}`));
    }
    return days;
  }
  renderWeeks() {
    const {
      maxDate,
      minDate,
      l10n,
      month,
      year
    } = this.props;
    const minimumDate = minDate || new Date(1970, 0, 1, 12);
    const maximumDate = maxDate || new Date(9999, 0, 1, 12);
    const minDateTime = minimumDate.getTime();
    const maxDateTime = maximumDate.getTime();
    const weeks = [];
    let date = new Date(year, month, 1, 12);
    let leadingBlanks = date.getDay() - this.firstDayOfWeekOffset;
    if (leadingBlanks < 0) {
      leadingBlanks = 7 + leadingBlanks;
    }
    const {
      selection,
      datePickerContent,
      getHolidayName
    } = this.props;
    const selectionStart = selection ? selection.start : undefined;
    const selectionEnd = selection ? selection.end : undefined;
    const {
      onSelectDate
    } = this;
    while (date.getMonth() === month) {
      const dateJsx = [];
      // We iterate every 7 days to build a row at a time
      for (let weekDate = 1; weekDate <= 7; weekDate += 1) {
        const monthChanged = date.getMonth() !== month;
        // First row can have some leading blank cells
        if (leadingBlanks > 0) {
          dateJsx.push(jsx("td", {
            "aria-hidden": true,
            className: getClassNameList$3('uitk-date-picker-day-number'),
            colSpan: leadingBlanks
          }, `${month}-leading${weekDate}`));
          weekDate = leadingBlanks;
          leadingBlanks = 0;
        } else if (!monthChanged) {
          const formattedISODate = l10n.formatDate(date, {
            dateFormat: 'iso-date',
            raw: 'yyyy-MM-dd'
          });
          const disabled = date.getTime() < minDateTime || date.getTime() > maxDateTime;
          // Valid day cells
          dateJsx.push(jsx("td", {
            className: this.generateClassNames(date),
            children: jsx(EGDSDay, {
              content: datePickerContent && datePickerContent[formattedISODate],
              date: date,
              disabled: disabled,
              formattedDate: String(date.getDate()),
              formattedFriendlyDate: l10n.formatDate(date, {
                dateFormat: 'long',
                skeleton: 'yMMMd'
              }),
              holidayName: getHolidayName && getHolidayName(date),
              onSelectDate: onSelectDate,
              selectionStart: selectionStart,
              selectionEnd: selectionEnd,
              selection: selection
            })
          }, `${month}-${date.getDate()}`));
          date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 12);
        }
      }
      weeks.push(jsx("tr", {
        children: dateJsx
      }, `${month}-weekEnding-${date.getTime()}`));
    }
    return weeks;
  }
  render() {
    const {
      l10n
    } = this.props;
    const yearMonthHeading = l10n.formatDate(this.date, {
      dateFormat: 'month-year',
      skeleton: 'yMMMM'
    });
    return jsxs("div", {
      className: getClassNameList$3('uitk-date-picker-month'),
      "data-stid": "date-picker-month",
      ref: this.monthRef,
      children: [jsx("h2", {
        className: getClassNameList$3('uitk-date-picker-month-name', 'uitk-type-medium'),
        children: yearMonthHeading
      }), jsxs("table", {
        className: getClassNameList$3('uitk-date-picker-weeks'),
        children: [jsx("thead", {
          children: jsx("tr", {
            children: this.renderDays()
          })
        }), jsx("tbody", {
          children: this.renderWeeks()
        })]
      })]
    });
  }
}
const EGDSMonth = props => {
  const l10n = useLocalization();
  return jsx(Month, {
    l10n: l10n,
    ...props
  });
};
EGDSMonth.displayName = 'EGDSMonth';
const EGDSNewMonth = EGDSMonth;
const getClassNameList$2 = classNames.bind(styles);
class EGDSDatePickerInput extends React.Component {
  static displayName = 'EGDSDatePickerInput';
  render() {
    const {
      cssClassNames,
      isDialog,
      onClick,
      selection,
      selectionDateMsg,
      singleMode,
      selectionDate
    } = this.props;
    const selectionButtonClasses = getClassNameList$2(cssClassNames, {
      'selection-filled': selection,
      'uitk-date-picker-input': selection && !isDialog
    });
    const grow = isDialog && !singleMode ? 1 : undefined;
    return jsx(EGDSLayoutFlexItem, {
      grow: grow,
      className: selectionButtonClasses,
      minWidth: singleMode ? 'half_width' : undefined,
      children: jsx("button", {
        type: "button",
        onClick: onClick,
        children: jsxs("span", {
          className: getClassNameList$2('uitk-date-picker-selection-date'),
          children: [selectionDate, selectionDateMsg && jsxs("span", {
            className: "is-visually-hidden",
            children: [": ", selectionDateMsg]
          })]
        })
      })
    });
  }
}
const EGDSNewDatePickerInput = EGDSDatePickerInput;
const getClassNameList$1 = classNames.bind(styles);
class DatePickerDialogView extends React.Component {
  static displayName = 'EGDSDatePickerDialogView';
  doneButtonRef;
  uid = Math.random().toString(36).substring(7);
  buttonText;
  constructor(props) {
    super(props);
    this.doneButtonRef = React.createRef();
    this.state = {
      localizedText: {
        cancelButton: () => props.l10n.formatText('datePicker.cancelButton.a11yLabel'),
        datePickerOpened: () => props.l10n.formatText('datePicker.datePickerOpened.liveAnnounce'),
        doneButtonFare: (nights, lowestFareMessage, startDate, endDate) => {
          if (nights <= 1) {
            return props.l10n.formatText('datePicker.doneButtonFareWithSummary.single.a11yLabel', startDate, lowestFareMessage);
          }
          return props.l10n.formatText('datePicker.doneButtonFareWithSummary.multiple.a11yLabel', startDate, endDate, lowestFareMessage);
        },
        clearInstructions: () => props.l10n.formatText('datePicker.clearInstructions.a11yLabel'),
        doneInstructions: () => props.l10n.formatText('datePicker.doneInstructions.a11yLabel'),
        hiddenDoneButton: (nights, startDate, endDate) => {
          if (nights <= 1) {
            return props.l10n.formatText('datePicker.doneButtonWithSummary.single.a11yLabel', startDate);
          }
          return props.l10n.formatText('datePicker.doneButtonWithSummary.multiple.a11yLabel', startDate, endDate);
        },
        // The "nightSummary" function is written like this to support older versions of uitk-react-localization (pre HoB)
        // if you are graduating this component please change this function to
        // nightSummary: (nights: number) => props.l10n.formatText('datePicker.nightSummary', nights),
        // and ensure teams are on the latest version of uitk-react-localization
        nightSummary: nights => props.l10n.formatText('datePicker.nightSummary', nights) || (nights <= 1 ? props.l10n.formatText('datePicker.nightSummary.single', nights) : props.l10n.formatText('datePicker.nightSummary.multiple', nights)),
        skipToButton: buttonName => props.l10n.formatText('datePicker.skipToButton', buttonName)
      },
      selection: props.selection
    };
    this.onClickTopStart = this.onClickTopStart.bind(this);
    this.onClickTopEnd = this.onClickTopEnd.bind(this);
    this.onSubmitClick = this.onSubmitClick.bind(this);
    this.onDismissClick = this.onDismissClick.bind(this);
    this.onSelectDate = this.onSelectDate.bind(this);
    this.internallyUpdateDatePicker = this.internallyUpdateDatePicker.bind(this);
    this.onChangeStartDate = this.onChangeStartDate.bind(this);
    this.onChangeEndDate = this.onChangeEndDate.bind(this);
    this.hiddenDoneButton = this.hiddenDoneButton.bind(this);
    this.skipToDoneButton = this.skipToDoneButton.bind(this);
  }
  componentDidMount() {
    const {
      localizedText
    } = this.state;
    liveAnnounce(localizedText.datePickerOpened(), Level.ASSERTIVE);
  }
  shouldComponentUpdate(nextProps) {
    if (nextProps && nextProps.holidays && nextProps.holidays.length > 0) {
      const {
        selection
      } = this.props;
      selection.configureHolidays(nextProps.holidays);
    }
    return true;
  }
  onClickTopStart() {
    const {
      onSelectStartMode
    } = this.props;
    onSelectStartMode();
    this.internallyUpdateDatePicker();
  }
  onClickTopEnd() {
    const {
      onSelectEndMode
    } = this.props;
    onSelectEndMode();
    this.internallyUpdateDatePicker();
  }
  onSubmitClick() {
    const {
      onSubmit,
      onDoneButtonClickCB
    } = this.props;
    if (onDoneButtonClickCB) {
      const {
        selection
      } = this.props;
      const {
        start,
        end
      } = selection;
      onDoneButtonClickCB(start, end);
    } else {
      onSubmit();
    }
  }
  onDismissClick() {
    const {
      onDismiss
    } = this.props;
    onDismiss();
  }
  onChangeStartDate(date) {
    const {
      onChangeStartDate
    } = this.props;
    if (onChangeStartDate) {
      onChangeStartDate(date, true);
    }
  }
  onChangeEndDate(date) {
    const {
      onChangeEndDate
    } = this.props;
    if (onChangeEndDate) {
      onChangeEndDate(date, false);
    }
  }
  /* Creating another state */
  onSelectDate() {
    this.internallyUpdateDatePicker();
  }
  // Workaround to having FullscreenDialog in the middle of the tree
  internallyUpdateDatePicker() {
    // eslint-disable-next-line react/no-access-state-in-setstate
    this.setState(this.state);
  }
  /* ********************** */
  skipToDoneButton() {
    const {
      localizedText
    } = this.state;
    const onClick = () => {
      if (this.doneButtonRef && this.doneButtonRef.current) {
        this.doneButtonRef.current.focus();
      }
    };
    return jsx("button", {
      type: "button",
      className: [getClassNameList$1('uitk-date-picker-skip-to-done'), 'is-visually-hidden'].join(' '),
      onClick: onClick,
      children: localizedText.skipToButton(this.buttonText)
    });
  }
  hiddenDoneButton() {
    const {
      formattedStartSelection,
      formattedEndSelection,
      selection
    } = this.props;
    const {
      localizedText
    } = this.state;
    const nights = selection.numberOfNights;
    const startDate = formattedStartSelection;
    const endDate = formattedEndSelection;
    const visuallyHiddenText = localizedText.hiddenDoneButton(nights, startDate, endDate);
    return jsx("button", {
      type: "button",
      className: "is-visually-hidden",
      onClick: this.onSubmitClick,
      children: visuallyHiddenText
    });
  }
  renderApplyButton(doneAnalytics, buttonText) {
    const {
      localizedText
    } = this.state;
    const {
      submitDisabled
    } = this.props;
    return jsx(EGDSFloatingFullWidthButton, {
      analytics: doneAnalytics,
      "data-stid": "apply-date-picker",
      disabled: submitDisabled,
      domRef: this.doneButtonRef,
      isFullWidth: true,
      onClick: this.onSubmitClick,
      size: EGDSButtonSize.LARGE,
      "aria-label": localizedText.doneInstructions(),
      children: buttonText
    });
  }
  render() {
    const {
      buttonText,
      clearButtonText,
      footerText,
      closeAnalytics,
      datePickerContent,
      doneAnalytics,
      endSelectedMsg,
      firstDayOfWeek,
      formattedStartSelection,
      lastValidDate,
      onScrollHandler,
      selectionClassNames,
      startSelectedMsg,
      singleMode,
      updateDatePicker,
      labels,
      l10n,
      holidays,
      firstValidDate,
      useClearDates,
      onClearDates
    } = this.props;
    this.buttonText = buttonText;
    const {
      selection
    } = this.state;
    const startSelection = selection.start;
    const endSelection = selection.end;
    const isDateSelected = singleMode ? startSelection : startSelection && endSelection;
    const startSelectionFormatted = startSelection ? l10n.formatDate(startSelection, {
      dateFormat: 'full',
      skeleton: 'MMMEd'
    }) : formattedStartSelection;
    const formattedEndSelection = endSelection ? l10n.formatDate(endSelection, {
      dateFormat: 'full',
      skeleton: 'MMMEd'
    }) : l10n.formatText('datePicker.dateInput.endDate.placeholder');
    const {
      localizedText
    } = this.state;
    const showHolidaysInfo = !!(selection.holidaysConfigured && selection.holidaysName !== '');
    const isScrim = !!(!footerText && !selection.holidaysConfigured);
    const scrimPaddingClassname = getClassNameList$1({
      'uitk-calendar-scrim': isScrim
    });
    const clearDatesButtonId = `clear-date-picker-${this.uid}`;
    if (!selection.holidaysName) {
      selection.holidaysName = [];
    }
    return jsxs(EGDSLayoutFlex, {
      direction: "column",
      className: getClassNameList$1('uitk-date-picker', 'date-picker-dialog'),
      children: [jsx(EGDSLayoutFlexItem, {
        children: jsx(EGDSToolbar, {
          className: getClassNameList$1('uitk-date-picker-toolbar'),
          navigationContent: {
            navIconLabel: localizedText.cancelButton(),
            onClick: this.onDismissClick,
            onClickAnalytics: closeAnalytics
          },
          ...(useClearDates && {
            toolbarActionContent: jsx(EGDSToolbarButton, {
              "aria-label": localizedText.clearInstructions(),
              buttonId: clearDatesButtonId,
              disabled: !isDateSelected,
              onClick: onClearDates,
              children: clearButtonText
            })
          }),
          children: jsxs(EGDSLayoutFlex, {
            alignItems: "center",
            className: getClassNameList$1('uitk-date-picker-selection-bar', 'uitk-date-picker-selection-bar-top'),
            tag: "section",
            children: [jsx(EGDSLayoutFlexItem, {
              children: jsx(EGDSDatePickerInput, {
                cssClassNames: selectionClassNames(true),
                isDialog: true,
                onClick: this.onClickTopStart,
                selectionDate: startSelectionFormatted,
                selectionDateMsg: `${labels.start} ${startSelectedMsg}`,
                selection: selection.start,
                singleMode: singleMode
              })
            }), !singleMode && jsxs(Fragment, {
              children: [jsx(EGDSLayoutFlexItem, {
                className: getClassNameList$1('uitk-date-picker-selection-icon'),
                children: jsx(EGDSIcon, {
                  name: "arrow_forward",
                  size: EGDSIconSize.SMALL
                })
              }), jsx(EGDSLayoutFlexItem, {
                children: jsx(EGDSDatePickerInput, {
                  cssClassNames: selectionClassNames(false),
                  isDialog: true,
                  onClick: this.onClickTopEnd,
                  selectionDate: formattedEndSelection,
                  selectionDateMsg: `${labels.end} ${endSelectedMsg}`,
                  selection: selection.end
                })
              })]
            }), this.hiddenDoneButton()]
          })
        })
      }), jsx(EGDSLayoutFlexItem, {
        children: jsx(EGDSCalendar, {
          buttonText: buttonText,
          className: scrimPaddingClassname,
          firstMonth: firstValidDate,
          skipToDoneButton: this.skipToDoneButton,
          datePickerContent: datePickerContent,
          isDialog: true,
          selection: selection,
          selectionStart: selection.start,
          selectionEnd: selection.end,
          startDate: firstValidDate,
          endDate: lastValidDate,
          updateDatePicker: updateDatePicker,
          firstDayOfWeek: firstDayOfWeek,
          onSelectDate: this.onSelectDate,
          singleMode: singleMode,
          holidays: holidays,
          onChangeStartDate: this.onChangeStartDate,
          onChangeEndDate: this.onChangeEndDate,
          onScrollHandler: onScrollHandler
        })
      }), jsxs(EGDSLayoutFlexItem, {
        children: [isScrim && jsx(EGDSScrim, {
          type: EGDSScrimType.BUTTON,
          className: getClassNameList$1('uitk-date-picker-footer'),
          children: this.renderApplyButton(doneAnalytics, buttonText)
        }), !isScrim && jsxs("div", {
          className: getClassNameList$1('uitk-date-picker-footer-opaque'),
          children: [showHolidaysInfo && jsx("div", {
            className: getClassNameList$1('uitk-date-picker-footer-text'),
            children: jsx("p", {
              children: selection.holidaysName
            })
          }), footerText && jsx("p", {
            className: getClassNameList$1('uitk-date-picker-footer-text', {
              'uitk-date-picker-footer-text-center': !showHolidaysInfo
            }),
            children: footerText
          }), this.renderApplyButton(doneAnalytics, buttonText)]
        })]
      })]
    });
  }
}
const EGDSDatePickerDialogView = props => {
  const l10n = useLocalization();
  return jsx(DatePickerDialogView, {
    l10n: l10n,
    ...props
  });
};
EGDSDatePickerDialogView.displayName = 'EGDSDatePickerDialogView';
const EGDSNewDatePickerDialogView = EGDSDatePickerDialogView;
const getClassNameList = classNames.bind(styles);
class DatePickerMenuView extends React.Component {
  static displayName = 'EGDSDatePickerMenuView';
  elementRef;
  constructor(props) {
    super(props);
    this.elementRef = React.createRef();
    this.state = {
      displayDate: undefined,
      localizedText: {
        cancelButton: () => props.l10n.formatText('datePicker.cancelButton.a11yLabel'),
        datePickerOpened: () => props.l10n.formatText('datePicker.datePickerOpened.liveAnnounce'),
        doneButtonFare: (nights, lowestFareMessage, startDate, endDate) => {
          if (nights <= 1) {
            return props.l10n.formatText('datePicker.doneButtonFareWithSummary.single.a11yLabel', startDate, lowestFareMessage);
          }
          return props.l10n.formatText('datePicker.doneButtonFareWithSummary.multiple.a11yLabel', startDate, endDate, lowestFareMessage);
        },
        clearInstructions: () => props.l10n.formatText('datePicker.clearInstructions.a11yLabel'),
        doneInstructions: () => props.l10n.formatText('datePicker.doneInstructions.a11yLabel'),
        hiddenDoneButton: (nights, startDate, endDate) => {
          if (nights <= 1) {
            return props.l10n.formatText('datePicker.doneButtonWithSummary.single.a11yLabel', startDate);
          }
          return props.l10n.formatText('datePicker.doneButtonWithSummary.multiple.a11yLabel', startDate, endDate);
        }
      }
    };
    this.onClickTopStart = this.onClickTopStart.bind(this);
    this.onClickTopEnd = this.onClickTopEnd.bind(this);
    this.onSubmitDates = this.onSubmitDates.bind(this);
    this.onChangeMonth = this.onChangeMonth.bind(this);
    this.onChangeStartDate = this.onChangeStartDate.bind(this);
    this.onChangeEndDate = this.onChangeEndDate.bind(this);
    this.clearDisplayDate = this.clearDisplayDate.bind(this);
    this.focusOnElement = this.focusOnElement.bind(this);
    this.hiddenDoneButton = this.hiddenDoneButton.bind(this);
  }
  componentDidMount() {
    const {
      formattedStartSelection,
      labels,
      preventAutoFocus,
      startSelectedMsg
    } = this.props;
    const {
      localizedText
    } = this.state;
    if (this.elementRef && this.elementRef.current) {
      const startInputRef = this.elementRef.current.getElementsByTagName('button')[0];
      if (!preventAutoFocus) {
        this.focusOnElement(startInputRef);
      }
    }
    const datePickerOpenedString = localizedText.datePickerOpened();
    liveAnnounce(`${datePickerOpenedString}, 
      ${formattedStartSelection} ${labels.start} ${startSelectedMsg}`);
  }
  shouldComponentUpdate(nextProps) {
    const {
      selection
    } = this.props;
    if (nextProps && nextProps.holidays && nextProps.holidays.length > 0) {
      selection.configureHolidays(nextProps.holidays);
    }
    return true;
  }
  onClickTopStart() {
    const {
      onSelectStartMode,
      selection
    } = this.props;
    this.setState({
      displayDate: selection.start
    });
    onSelectStartMode();
  }
  onClickTopEnd() {
    const {
      onSelectEndMode,
      selection
    } = this.props;
    this.setState({
      displayDate: selection.end
    });
    onSelectEndMode();
  }
  onSubmitDates() {
    const {
      onSubmit,
      onDoneButtonClickCB
    } = this.props;
    if (onDoneButtonClickCB) {
      const {
        selection
      } = this.props;
      const {
        start,
        end
      } = selection;
      onDoneButtonClickCB(start, end);
    } else {
      onSubmit();
    }
  }
  onChangeMonth(month) {
    const {
      onChangeMonth
    } = this.props;
    if (onChangeMonth) {
      onChangeMonth(month, false);
    }
  }
  onChangeStartDate(date) {
    const {
      onChangeStartDate
    } = this.props;
    if (onChangeStartDate) {
      onChangeStartDate(date, false);
    }
  }
  onChangeEndDate(date) {
    const {
      onChangeEndDate
    } = this.props;
    if (onChangeEndDate) {
      onChangeEndDate(date, false);
    }
  }
  clearDisplayDate() {
    this.setState({
      displayDate: undefined
    });
  }
  focusOnElement(ref) {
    if (isElementVisible(ref)) {
      ref.focus();
    }
  }
  hiddenDoneButton() {
    const {
      formattedStartSelection,
      formattedEndSelection,
      selection
    } = this.props;
    const {
      localizedText
    } = this.state;
    const nights = selection.numberOfNights;
    const visuallyHiddenText = localizedText.hiddenDoneButton(nights, formattedStartSelection, formattedEndSelection);
    return jsx("button", {
      type: "button",
      className: "is-visually-hidden",
      onClick: this.onSubmitDates,
      children: visuallyHiddenText
    });
  }
  render() {
    const {
      buttonText,
      clearButtonText,
      datePickerContent,
      doneAnalytics,
      doubleMonths,
      endSelectedMsg,
      firstDayOfWeek,
      firstValidDate,
      footerText,
      formattedEndSelection,
      formattedStartSelection,
      holidays,
      labels,
      lastValidDate,
      nextPageAnalytics,
      onClearDates,
      previousPageAnalytics,
      selection,
      selectionClassNames,
      singleMode,
      startSelectedMsg,
      submitDisabled,
      updateDatePicker,
      useClearDates
    } = this.props;
    const {
      localizedText,
      displayDate
    } = this.state;
    const monthsToRender = doubleMonths ? 2 : 1;
    const isDateSelected = singleMode ? selection.start : selection.start && selection.end;
    return jsxs("div", {
      className: getClassNameList('uitk-date-picker', 'date-picker-menu'),
      ref: this.elementRef,
      children: [jsxs(EGDSLayoutFlex, {
        alignItems: "center",
        className: getClassNameList('uitk-date-picker-selection-bar', 'uitk-date-picker-selection-bar-top'),
        tag: "section",
        children: [jsx(EGDSLayoutFlexItem, {
          children: jsx(EGDSDatePickerInput, {
            cssClassNames: selectionClassNames(true),
            isDialog: false,
            onClick: this.onClickTopStart,
            selectionDate: formattedStartSelection,
            selectionDateMsg: `${labels.start} ${startSelectedMsg}`,
            selection: selection.start,
            singleMode: singleMode
          })
        }), !singleMode && jsx(EGDSLayoutFlexItem, {
          className: getClassNameList('uitk-date-picker-selection-icon'),
          children: jsx(EGDSIcon, {
            name: "arrow_forward",
            size: EGDSIconSize.SMALL
          })
        }), !singleMode && jsx(EGDSLayoutFlexItem, {
          children: jsx(EGDSDatePickerInput, {
            cssClassNames: selectionClassNames(false),
            isDialog: false,
            onClick: this.onClickTopEnd,
            selectionDate: formattedEndSelection,
            selectionDateMsg: `${labels.end} ${endSelectedMsg}`,
            selection: selection.end
          })
        }), this.hiddenDoneButton()]
      }), jsx(EGDSCalendar, {
        buttonText: buttonText,
        datePickerContent: datePickerContent,
        firstMonth: selection.start || firstValidDate,
        monthsToRender: monthsToRender,
        isDialog: false,
        selection: selection,
        selectionStart: selection.start,
        selectionEnd: selection.end,
        startDate: firstValidDate,
        endDate: lastValidDate,
        updateDatePicker: updateDatePicker,
        displayDate: displayDate,
        clearDisplayDate: this.clearDisplayDate,
        firstDayOfWeek: firstDayOfWeek,
        singleMode: singleMode,
        nextPageAnalytics: nextPageAnalytics,
        previousPageAnalytics: previousPageAnalytics,
        holidays: holidays,
        onChangeMonth: this.onChangeMonth,
        onChangeStartDate: this.onChangeStartDate,
        onChangeEndDate: this.onChangeEndDate
      }), jsxs(EGDSLayoutFlex, {
        className: getClassNameList('uitk-date-picker-menu-footer'),
        children: [(footerText || selection.holidaysConfigured && selection.holidaysName !== '') && [jsx(EGDSLayoutFlexItem, {
          className: getClassNameList('uitk-date-picker-footer-text'),
          children: jsxs("div", {
            children: [selection.holidaysConfigured && selection.holidaysName !== '' && jsx("p", {
              children: selection.holidaysName
            }, "holiday"), footerText && jsx("p", {
              children: footerText
            }, "legal")]
          })
        }, "0")], jsx(EGDSLayoutFlexItem, {
          className: getClassNameList('dialog-done'),
          shrink: 0,
          children: jsxs("div", {
            children: [useClearDates && jsx(EGDSSpacing, {
              margin: {
                inline: 'three'
              },
              children: jsx(EGDSTertiaryButton, {
                "aria-label": localizedText.clearInstructions(),
                disabled: !isDateSelected,
                onClick: onClearDates,
                "data-stid": "clear-date-picker",
                children: clearButtonText
              })
            }), jsx(EGDSPrimaryButton, {
              "aria-label": localizedText.doneInstructions(),
              analytics: doneAnalytics,
              onClick: this.onSubmitDates,
              "data-stid": "apply-date-picker",
              disabled: submitDisabled,
              children: buttonText
            })]
          })
        })]
      })]
    });
  }
}
const EGDSDatePickerMenuView = props => {
  const l10n = useLocalization();
  return jsx(DatePickerMenuView, {
    l10n: l10n,
    ...props
  });
};
EGDSDatePickerMenuView.displayName = 'EGDSDatePickerMenuView';
const EGDSNewDatePickerMenuView = EGDSDatePickerMenuView;
export { DatePickerModel as D, EGDSCalendar as E, EGDSNewCalendar as a, EGDSDatePickerDateFormat as b, EGDSDateField as c, EGDSNewDateField as d, EGDSDatePickerTriggeredSelector as e, EGDSDatePickerRenderType as f, DatePicker as g, EGDSDatePicker as h, EGDSNewDatePicker as i, DayName as j, EGDSDay as k, EGDSNewDay as l, EGDSMonth as m, EGDSNewMonth as n, EGDSDatePickerInput as o, EGDSNewDatePickerInput as p, EGDSDatePickerDialogView as q, EGDSNewDatePickerDialogView as r, EGDSDatePickerMenuView as s, EGDSNewDatePickerMenuView as t };